import type { JwtToken } from '../types/global';
import { fetchAndParse } from './api';

export enum ApprovalConfigurationStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Denied = 'Denied',
    Canceled = 'Canceled',
}

export type ApprovalTaskStatus = 'Pending' | 'Approved' | 'Denied' | 'Expired' | 'Canceled';

export type ApprovalTaskStatusWithAny = ApprovalTaskStatus | 'Any';

export interface ListTask {
    id: string;
    status: ApprovalTaskStatus;
    accountId: string;
    title: string;
    createdBy: string;
    createdBySystem: string;
    expiresAt: string;
    content: string;
    createdAt: string;
    updatedAt: string;
    canApprove: boolean;
    // Animation states for UI
    isNewlyAdded?: boolean;
    isRemoving?: boolean;
}

export interface ApprovalEvent {
    id: string;
    accountId?: string;
    taskId?: string;
    approvalConfigurationDetailId?: string;
    createdBy: string;
    comment: string | null;
    status: ApprovalConfigurationStatus;
    createdAt: string;
    updatedAt: string;
}

export type ApprovalConfiguration = {
    id: string;
    accountId: string;
    taskId: string;
    details: ApprovalConfigurationDetails[];
    approvalComparisonType: ApprovalComparisonType;
    denialComparisonType: ApprovalComparisonType;
    onApprovedActionId: string;
    onDeniedActionId: string;
    order: number;
    escalationTime: Date;
    createdAt: Date;
    updatedAt: Date;
};

export type ApprovalConfigurationDetails = {
    id: string;
    accountId: string;
    taskId: string;
    approvalConfigurationId: string;
    emails: string[];
    roles: string[];
    minimumApprovals: number;
    minimumDenials: number;
    status: ApprovalConfigurationStatus;
    createdAt: Date;
    updatedAt: Date;
};

export type ApprovalComparisonType = 'all' | 'any';

export interface DetailTask extends ListTask {
    approvalEvents: ApprovalEvent[];
    approvalConfigurations: ApprovalConfiguration[];
}

export interface Paging {
    total: number;
    page: number;
    pageSize: number;
    orderBy: string;
    sortOrder: string;
}

export interface InboxTasks extends Paging {
    tasks: ListTask[];
}

export interface InboxTasksRequest {
    page?: number;
    pageSize?: number;
    orderBy?: string;
    sortOrder?: string;
    status?: ApprovalTaskStatusWithAny;
    isWatchingTasks?: boolean;
}

// Helper function to convert single status to API array format
const statusToApiArray = (status: ApprovalTaskStatusWithAny): ApprovalTaskStatus[] => {
    return status === 'Any' ? ['Pending', 'Approved', 'Denied', 'Expired', 'Canceled'] : [status];
};

export const getTasks = async (request: InboxTasksRequest, jwt: JwtToken): Promise<InboxTasks> => {
    const apiRequest = {
        ...request,
        status: request.status ? statusToApiArray(request.status) : undefined,
    };

    return await fetchAndParse('/api/inbox/tasks', 'post', jwt, apiRequest);
};

export const getApprovalTask = async (id: string, jwt: JwtToken): Promise<DetailTask> =>
    fetchAndParse(`/api/inbox/task/${id}`, 'get', jwt);

export const approveTask = async (
    id: string,
    comment: string | null,
    jwt: JwtToken,
): Promise<void> => fetchAndParse(`/api/approval/task/${id}/approve`, 'post', jwt, { comment });

export const denyTask = async (id: string, comment: string | null, jwt: JwtToken): Promise<void> =>
    fetchAndParse(`/api/approval/task/${id}/deny`, 'post', jwt, { comment });
